// frontend/src/App.jsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import NewHeader from './components/newui/NewHeader';
import NewFooter from './components/newui/NewFooter';
import ScrollToTop from './components/ScrollToTop';
import NotificationContainer from './components/NotificationContainer';

import NewUIHome from './pages/NewUIHome';
import NewUIProfile from './pages/NewUIProfile';
import NewUIGames from './pages/NewUIGames';
import NewUIGameDetail from './pages/NewUIGameDetail';
import NewUIGamePage from './pages/NewUIGamePage';
import NewUINextGames from './pages/NewUINextGames';
import NewUITournamentDetail from './pages/NewUITournamentDetail';
import NewUILogin from './pages/NewUILogin';
import NewUIRegister from './pages/NewUIRegister';
import NewUIRegistrationSuccess from './pages/NewUIRegistrationSuccess';
import NewUIResetPassword from './pages/NewUIResetPassword';
import NewUIForgotPassword from './pages/NewUIForgotPassword';
import NewUITerms from './pages/NewUITerms';
import NewUIVerifyEmail from './pages/NewUIVerifyEmail';
import NewUIRecharge from './pages/NewUIRecharge';
import NewUIActiveMatches from './pages/NewUIActiveMatches';
import NewUIGoogleAuthSuccess from './pages/NewUIGoogleAuthSuccess';
import NewProtectedRoute from './components/newui/NewProtectedRoute';
import NewRedirectIfAuthenticated from './components/newui/NewRedirectIfAuthenticated';

import GoogleOAuthCallbackBridge from './pages/GoogleOAuthCallbackBridge';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';
import PaymentPending from './pages/PaymentPending';
import MockPaymentPage from './pages/MockPaymentPage';

import AdminRoute from './components/admin/AdminRoute';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminTournaments from './pages/admin/Tournaments';
import AdminTransactions from './pages/admin/Transactions';
import AdminStats from './pages/admin/Stats';
import AdminSettings from './pages/admin/Settings';

const AppContent = () => {
    const { isAuthenticated, user, authStateVersion } = useAuth();
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');

    useEffect(() => {
        console.log('App re-render - Estado global auth:', {
            isAuthenticated,
            user: user ? { id: user.id, username: user.username } : null,
            authStateVersion,
            pathname: window.location.pathname
        });
    }, [isAuthenticated, user, authStateVersion]);

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#0A1929' }}>
            <ScrollToTop />
            {!isAdminRoute && <NewHeader key={`new-header-${authStateVersion}`} />}
            <NotificationContainer />

            <Routes>
                {/* Página principal */}
                <Route path="/" element={<NewUIHome />} />

                {/* Autenticación */}
                <Route path="/login" element={
                    <NewRedirectIfAuthenticated>
                        <NewUILogin />
                    </NewRedirectIfAuthenticated>
                } />
                <Route path="/register" element={
                    <NewRedirectIfAuthenticated>
                        <NewUIRegister />
                    </NewRedirectIfAuthenticated>
                } />
                <Route path="/forgot-password" element={
                    <NewRedirectIfAuthenticated>
                        <NewUIForgotPassword />
                    </NewRedirectIfAuthenticated>
                } />
                <Route path="/reset-password" element={
                    <NewRedirectIfAuthenticated>
                        <NewUIResetPassword />
                    </NewRedirectIfAuthenticated>
                } />
                <Route path="/verify-email" element={<NewUIVerifyEmail />} />
                <Route path="/registration-success" element={<NewUIRegistrationSuccess />} />
                <Route path="/auth/google-success" element={<NewUIGoogleAuthSuccess />} />
                <Route path="/auth/complete/google-oauth" element={<GoogleOAuthCallbackBridge />} />

                {/* Páginas protegidas */}
                <Route path="/profile" element={
                    <NewProtectedRoute>
                        <NewUIProfile />
                    </NewProtectedRoute>
                } />
                <Route path="/recharge" element={
                    <NewProtectedRoute>
                        <NewUIRecharge />
                    </NewProtectedRoute>
                } />
                <Route path="/games/truco/game/:gameId" element={
                    <NewProtectedRoute>
                        <NewUIGamePage />
                    </NewProtectedRoute>
                } />
                <Route path="/games/:slug" element={
                    <NewProtectedRoute>
                        <NewUIGameDetail />
                    </NewProtectedRoute>
                } />
                <Route path="/games" element={
                    <NewProtectedRoute>
                        <NewUIGames />
                    </NewProtectedRoute>
                } />
                <Route path="/tournaments/:id" element={
                    <NewProtectedRoute>
                        <NewUITournamentDetail />
                    </NewProtectedRoute>
                } />
                <Route path="/next-games" element={
                    <NewProtectedRoute>
                        <NewUINextGames />
                    </NewProtectedRoute>
                } />
                <Route path="/active-matches" element={
                    <NewProtectedRoute>
                        <NewUIActiveMatches />
                    </NewProtectedRoute>
                } />

                {/* Páginas públicas */}
                <Route path="/terms" element={<NewUITerms />} />

                {/* Resultado de pagos */}
                <Route path="/payment/success" element={
                    <NewProtectedRoute>
                        <PaymentSuccess />
                    </NewProtectedRoute>
                } />
                <Route path="/payment/failure" element={
                    <NewProtectedRoute>
                        <PaymentFailure />
                    </NewProtectedRoute>
                } />
                <Route path="/payment/pending" element={
                    <NewProtectedRoute>
                        <PaymentPending />
                    </NewProtectedRoute>
                } />
                <Route path="/payment/mock" element={
                    <NewProtectedRoute>
                        <MockPaymentPage />
                    </NewProtectedRoute>
                } />

                {/* Administración */}
                <Route path="/admin" element={
                    <AdminRoute>
                        <AdminDashboard />
                    </AdminRoute>
                } />
                <Route path="/admin/users" element={
                    <AdminRoute>
                        <AdminUsers />
                    </AdminRoute>
                } />
                <Route path="/admin/tournaments" element={
                    <AdminRoute>
                        <AdminTournaments />
                    </AdminRoute>
                } />
                <Route path="/admin/transactions" element={
                    <AdminRoute>
                        <AdminTransactions />
                    </AdminRoute>
                } />
                <Route path="/admin/stats" element={
                    <AdminRoute>
                        <AdminStats />
                    </AdminRoute>
                } />
                <Route path="/admin/settings" element={
                    <AdminRoute>
                        <AdminSettings />
                    </AdminRoute>
                } />
            </Routes>

            {!isAdminRoute && <NewFooter />}
        </div>
    );
};

function App() {
    return (
        <Router /*basename="/manoamano"*/>
            <AuthProvider>
                <SocketProvider>
                    <AppContent />
                </SocketProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
